import { Bar, BarChart, CartesianGrid, XAxis, YAxis } from "recharts";
import { Skeleton } from "@/components/kit";
import {
  type ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart";
import type { ProfitByDayRowDTO } from "@/modules/reports/domain/types";
import { formatLak } from "@/shared/lib/currency";

const chartConfig = {
  netProfit: {
    label: "ກຳໄລສຸດທິ",
    color: "var(--chart-2)",
  },
} satisfies ChartConfig;

type ProfitChartProps = {
  rows?: ProfitByDayRowDTO[];
  isLoading?: boolean;
};

export function ProfitChart({ rows = [], isLoading }: ProfitChartProps) {
  if (isLoading) {
    return <Skeleton className="h-[280px] w-full rounded-2xl" />;
  }

  if (rows.length === 0) {
    return (
      <div className="brand-card flex h-[280px] items-center justify-center p-6 text-muted-foreground text-sm">
        ບໍ່ມີຂໍ້ມູນໃນຊ່ວງນີ້
      </div>
    );
  }

  const data = rows.map((r) => ({
    date: r.date.slice(5),
    grossProfit: Number(r.grossProfit),
    netProfit: Number(r.netProfit),
    revenue: Number(r.revenue),
    cogs: Number(r.cogs),
    expenses: Number(r.expenses),
  }));

  return (
    <div className="brand-card p-4 sm:p-5">
      <h2 className="mb-4 font-display text-lg">ກຳໄລສຸດທິຕາມວັນ</h2>
      <ChartContainer
        config={chartConfig}
        className="aspect-auto h-[240px] w-full"
      >
        <BarChart data={data} margin={{ left: 8, right: 8, top: 8 }}>
          <CartesianGrid vertical={false} />
          <XAxis
            dataKey="date"
            tickLine={false}
            axisLine={false}
            tickMargin={8}
          />
          <YAxis
            tickLine={false}
            axisLine={false}
            tickFormatter={(v) => `${Math.round(Number(v) / 1000)}k`}
            width={40}
          />
          <ChartTooltip
            content={
              <ChartTooltipContent
                formatter={(value, _name, item) => (
                  <div className="flex flex-col gap-1">
                    <span className="font-medium">
                      {formatLak(String(value))}
                    </span>
                    <span className="text-muted-foreground text-xs">
                      ລາຍຮັບ {formatLak(String(item.payload.revenue))}
                    </span>
                    <span className="text-muted-foreground text-xs">
                      COGS {formatLak(String(item.payload.cogs))}
                    </span>
                    <span className="text-muted-foreground text-xs">
                      ລາຍຈ່າຍ {formatLak(String(item.payload.expenses))}
                    </span>
                    <span className="text-muted-foreground text-xs">
                      Gross {formatLak(String(item.payload.grossProfit))}
                    </span>
                  </div>
                )}
              />
            }
          />
          <Bar
            dataKey="netProfit"
            fill="var(--color-netProfit)"
            radius={[6, 6, 0, 0]}
          />
        </BarChart>
      </ChartContainer>
    </div>
  );
}
